'use client'

import { PaymentType } from '@server/schemas'
import { useI18n } from '@shared/config/i18n'
import type { Locale } from '@shared/config/i18n'
import React, { memo } from 'react'
import { match, P } from 'ts-pattern'

import { Box, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  paymentType: PaymentType
  paymentDay: number
  originalPrice: number
  discountPrice: number
  totalPrice: number
  installmentPeriod: number
  installmentPrice: number
  currency?: string
  showInstallment?: boolean
}

const PRICE_FORMAT_LOCALE: Record<Locale, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  ja: 'ja-JP',
}

const formatAmount = (value: number, currency: string, locale: Locale) => {
  const code = currency.toUpperCase()

  try {
    return new Intl.NumberFormat(PRICE_FORMAT_LOCALE[locale], {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value)
  } catch {
    return `${value.toLocaleString(PRICE_FORMAT_LOCALE[locale])} ${code}`
  }
}

const PaymentSummary = memo(
  ({
    paymentType,
    originalPrice,
    discountPrice,
    totalPrice,
    installmentPeriod,
    installmentPrice,
    paymentDay,
    currency = 'KRW',
    showInstallment = true,
  }: Props) => {
    const { effectiveLocale, t } = useI18n()
    const isDiscount = discountPrice > 0
    const originalAmount = formatAmount(originalPrice, currency, effectiveLocale)
    const discountAmount = formatAmount(discountPrice, currency, effectiveLocale)
    const totalAmount = formatAmount(totalPrice, currency, effectiveLocale)
    const installmentAmount = formatAmount(installmentPrice, currency, effectiveLocale)

    return (
      <VStack className="p-5 bg-gray-100 border border-gray-200 rounded-lg gap-4">
        <VStack className="gap-1">
          {match(paymentType)
            .with(P.union('SUBSCRIBE'), () => (
              <HStack className="justify-between">
                <Typography size="body1" className="text-gray-400 font-normal">
                  {t('payment.summary.monthlyPaymentDay')}
                </Typography>
                <Typography size="body1" className="font-normal">
                  {t('payment.summary.dayOfMonth', { day: paymentDay })}
                </Typography>
              </HStack>
            ))
            .otherwise(() => null)}

          {match(paymentType)
            .with(P.union('SUBSCRIBE'), () => (
              <React.Fragment>
                <HStack className="justify-between">
                  <Typography size="body1" className="text-gray-400 font-normal">
                    {t('payment.summary.productAmount')}
                  </Typography>
                  <Typography size="body1" className="font-normal">
                    {t('payment.summary.monthlyAmount', { amount: originalAmount })}
                  </Typography>
                </HStack>

                <HStack className="justify-between">
                  <Typography size="body1" className="text-gray-400 font-normal">
                    {t('payment.summary.couponDiscount')}
                  </Typography>
                  <Typography size="body1" className="font-normal">
                    {isDiscount
                      ? t('payment.summary.monthlyDiscountAmount', { amount: discountAmount })
                      : t('payment.summary.zeroAmount')}
                  </Typography>
                </HStack>
              </React.Fragment>
            ))
            .with(P.union('LUMP_SUM', 'IPAD'), () => (
              <React.Fragment>
                <HStack className="justify-between">
                  <Typography size="body1" className="text-gray-400 font-normal">
                    {t('payment.summary.productAmount')}
                  </Typography>
                  <Typography size="body1" className="font-normal">
                    {originalAmount}
                  </Typography>
                </HStack>

                <HStack className="justify-between">
                  <Typography size="body1" className="text-gray-400 font-normal">
                    {t('payment.summary.couponDiscount')}
                  </Typography>
                  <Typography size="body1" className="font-normal">
                    {isDiscount
                      ? t('payment.summary.discountAmount', { amount: discountAmount })
                      : t('payment.summary.zeroAmount')}
                  </Typography>
                </HStack>
              </React.Fragment>
            ))
            .otherwise(() => null)}

          {match(paymentType)
            .with(P.union('IPAD'), () => (
              <HStack className="justify-between">
                <Typography size="body1" className="text-gray-400 font-normal">
                  {t('payment.summary.shippingFee')}
                </Typography>
                <Typography size="body1" className="font-normal">
                  {t('payment.summary.free')}
                </Typography>
              </HStack>
            ))
            .otherwise(() => null)}
        </VStack>

        <Box className="h-px bg-gray-200" />

        <VStack className="gap-1">
          {match(paymentType)
            .with(P.union('SUBSCRIBE'), () => (
              <HStack className="justify-between">
                <Typography size="h2">{t('payment.summary.totalAmount')}</Typography>
                <Typography size="h2">{t('payment.summary.monthlyAmount', { amount: totalAmount })}</Typography>
              </HStack>
            ))
            .with(P.union('LUMP_SUM', 'IPAD'), () => (
              <React.Fragment>
                <HStack className="justify-between">
                  <Typography size="body1" className="text-gray-400 font-normal">
                    {t('payment.summary.totalAmount')}
                  </Typography>
                  <Typography size="body1" className="font-normal">
                    {totalAmount}
                  </Typography>
                </HStack>
                {showInstallment && (
                  <HStack className="justify-between">
                    <Typography size="body1" className="text-gray-400 font-normal">
                      {t('payment.summary.installmentLabel', { months: installmentPeriod })}
                    </Typography>
                    <Typography size="h2">{installmentAmount}</Typography>
                  </HStack>
                )}
              </React.Fragment>
            ))
            .otherwise(() => null)}
        </VStack>
      </VStack>
    )
  },
)

export default PaymentSummary
